<!--
  @fileoverview File Upload Item Component - Individual File Handler

  Displays and validates a single file in the upload queue including:
  - File preview (image thumbnail or document icon)
  - Virus scanning status and results
  - File type validation
  - Error message display

  @component UploaderFile
  @displayName File Upload Item

  Features:
  - Image preview for image files
  - Document icon for non-image files
  - Real-time virus scanning with status indicator
  - File validation with detailed error messages
  - Emits status updates to parent component

  @example
  <File
    v-model="fileName"
    :file="fileObject"
    :preview="previewUrl"
    @update-is-busy="handleBusy"
    @update-antivirus-failed="handleAntivirusResult"
    @update-is-file-type-invalid="handleValidation"
  />
-->
<template>
  <div>
    <p>
      <strong>{{ modelValue }}</strong>
    </p>
    <div class="flex">
      <img
        v-if="isImageFile(modelValue)"
        :src="preview"
        class="min-w-[180px] max-h-[150px] w-[200px]"
      />
      <el-icon v-if="!isImageFile(modelValue)" class="text-[40px] py-5 px-[50px]"><Document /></el-icon>

      <!-- errors -->
      <template>
        <ul class="list-none p-0 mx-[10px] my-0">
          <template v-if="fileNotValidForScan">
            <li class="text-xs mb-[5px] mt-0 mx-0 text-left">
              <div class="flex items-center">
                <el-icon class="mr-[5px]"><WarningFilled /></el-icon>
                File is not valid for scan.
              </div>
            </li>
          </template>
          <template v-else-if="isBusy">
            <li class="text-xs mb-[5px] mt-0 mx-0 text-left">
              <div class="flex items-center">
                <el-icon class="is-loading mr-[5px]"><Loading /></el-icon>
                Pending: Scanning for virus...
              </div>
            </li>
          </template>
          <template v-else-if="isFileContainVirus">
            <li class="text-xs text-red-500 mb-[5px] mt-0 mx-0 text-left">
              <div class="flex items-center">
                <el-icon class="mr-[5px]"><WarningFilled /></el-icon>
                Invalid: File contain suspicious malware, please try with other file.
              </div>
            </li>
          </template>
          <template v-else-if="isValid">
            <li class="text-xs mb-[5px] mt-0 mx-0 text-left text-[#62c23a]">
              <div class="flex items-center">
                <el-icon class="mr-[5px]"><CircleCheck /></el-icon>
                <span class="break-words">
                  Success: Please press confirm to continue uploading the file.
                </span>
              </div>
            </li>
          </template>

          <li
            class="text-xs text-red-500 mb-[5px] mt-0 mx-0 text-left break-words"
            v-for="(err, errIndex) in fileUploadErrors"
            :key="`file-upload-error-${errIndex}`"
          >
            <div class="flex items-center">
              <el-icon class="mr-[5px]"><WarningFilled /></el-icon>
              {{ err }}
            </div>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/FileUploader/File
 * @description Individual file item with validation and virus scanning
 */
import { ref, computed, onMounted } from "vue";
import { Document, WarningFilled, Loading, CircleCheck } from "@element-plus/icons-vue";
import { validateFile } from "@/helpers/validateFile";
import axios from "@/helpers/axios";
import * as _ from "lodash-es";
import { useMainStore } from "@/stores/main";

/**
 * Component props
 */
const props = defineProps({
  /** File name (v-model) */
  modelValue: {
    type: String,
    default: "",
  },
  /** Preview URL (blob URL for images) */
  preview: {
    type: String,
    default: "",
  },
  /** File object to upload */
  file: {
    type: File,
    default: null,
  },
});

/**
 * Component emits
 */
const emit = defineEmits([
  "update:modelValue",
  "update-is-busy",
  "update-antivirus-failed",
  "update-is-file-type-invalid",
]);

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Component local state
 */
/** Whether virus scan is in progress */
const isBusy = ref(false);
/** Whether file contains virus */
const isFileContainVirus = ref(false);
/** Whether file is not valid for virus scan */
const fileNotValidForScan = ref(false);
/** Array of validation error messages */
const fileUploadErrors = ref([]);
/** Whether file passed all validations */
const fileIsValidForUpload = ref(false);

/**
 * Access webchat settings from Pinia store
 * @returns {Object} Webchat configuration settings
 */
const settings = computed(() => store.settings);

/**
 * Check if file is valid for upload
 *
 * A file is valid when it has passed all validation checks.
 *
 * @returns {boolean} True if file can be uploaded
 */
const isValid = computed(() => {
  return fileIsValidForUpload.value;
});

/**
 * Check if filename represents an image file
 *
 * Uses regex to check for common image extensions (jpg, jpeg, png).
 *
 * @param {string} url - Filename or URL to check
 * @returns {boolean} True if file is an image
 */
function isImageFile(url) {
  const imageRegex = new RegExp("(jpg|jpeg)|(png)", "g");
  return imageRegex.test(url);
}

/**
 * Scan file for viruses
 *
 * Uploads file to server's virus scanning endpoint.
 * Updates component state based on scan results.
 * Emits status updates to parent component.
 */
function scanVirus() {
  if (!isBusy.value) {
    isBusy.value = true;
    emit("update-is-busy", true);
    isFileContainVirus.value = false;
    const formData = new FormData();
    formData.append("file", props.file);
    axios
      .post(`${store.server}/api/webchat_virus_scan`, formData)
      .then((response) => {
        const result = _.get(response, "data", {});
        const isSafe = !result.is_infected;
        const noVirusDetected = _.isEmpty(result.viruses);
        if (isSafe && noVirusDetected) {
          isFileContainVirus.value = false;
          emit("update-antivirus-failed", false);
        }
      })
      .catch((err) => {
        console.error(err.message);
        isFileContainVirus.value = false;
        emit("update-antivirus-failed", true);
      })
      .finally(() => {
        emit("update-is-busy", false);
        isBusy.value = false;
      });
  }
}

/**
 * Expose isValid for parent component access
 *
 * Parent component (Index.vue) needs to check if all files are valid
 * before enabling the confirm button.
 */
defineExpose({
  isValid,
});

/**
 * Validate file on component creation
 *
 * Runs file validation to check type, size, etc.
 * Sets validation state for UI display.
 */
validateFile(props.file).then(({ isValid, errors }) => {
  fileIsValidForUpload.value = isValid;
  fileUploadErrors.value = !isValid ? errors : [];
});

/**
 * Validate file and start virus scan on mount
 *
 * If file is valid for scanning, initiates virus scan.
 * If file is not valid, marks it as not valid for scan.
 */
onMounted(() => {
  validateFile(props.file).then(({ isValid, errors }) => {
    emit("update-is-file-type-invalid", !isValid);
    const fileNotValid = !isValid;
    if (fileNotValid) {
      fileNotValidForScan.value = true;
      return;
    }
    scanVirus();
  });
});
</script>
